/**
 * Audit Logs API Service - EVOP Inspector
 */

import { apiClient } from './api'
import { downloadBlob } from '../utils/download'

// Audit log types
export interface AuditLog {
  id: number
  event_id: string
  event_type: string
  event_category: string
  user_id?: number
  user_email?: string
  user_role?: string
  session_id?: string
  ip_address?: string
  user_agent?: string
  request_id?: string
  request_method?: string
  request_path?: string
  action: string
  resource_type?: string
  resource_id?: string
  resource_name?: string
  fp_id?: number
  fp_code?: string
  details?: Record<string, any>
  old_values?: Record<string, any>
  new_values?: Record<string, any>
  success: string
  severity: string
  sub_type?: string
  error_message?: string
  duration_ms?: number
  duration_formatted?: string
  created_at: string
}

export interface AuditLogListResponse {
  logs: AuditLog[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface AuditStats {
  total_events: number
  events_today: number
  events_this_week: number
  events_this_month: number
  failed_events: number
  unique_users: number
  most_active_users: Array<{
    email: string
    count: number
  }>
  event_type_breakdown: Record<string, number>
  event_category_breakdown: Record<string, number>
}

export interface AuditEventTypeOption {
  value: string
  label: string
  category: string
  severity_default: string
}

export interface AuditEventTypesResponse {
  event_types: AuditEventTypeOption[]
  categories: string[]
  severities: string[]
}

export interface AuditFilters {
  page?: number
  page_size?: number
  user_email?: string
  event_type?: string
  event_category?: string
  resource_type?: string
  fp_code?: string
  success?: string
  severity?: string
  include_system?: boolean
  start_date?: string
  end_date?: string
  search?: string
}

class AuditApi {
  /**
   * Get audit logs with filtering and pagination
   */
  async getLogs(filters: AuditFilters = {}): Promise<AuditLogListResponse> {
    const response = await apiClient.get<AuditLogListResponse>('/admin/audit/logs', {
      params: filters
    })
    return response.data
  }

  /**
   * Get audit statistics
   */
  async getStats(): Promise<AuditStats> {
    const response = await apiClient.get<AuditStats>('/admin/audit/stats')
    return response.data
  }

  /**
   * Get available event types and categories for filtering
   */
  async getEventTypes(): Promise<AuditEventTypesResponse> {
    const response = await apiClient.get<AuditEventTypesResponse>('/admin/audit/event-types')
    return response.data
  }

  /**
   * Fire-and-forget report of a client-side 404 so support can see who hit a
   * dead/stale URL. Never throws - the 404 page must not itself error.
   */
  async reportRouteNotFound(path: string): Promise<void> {
    try {
      await apiClient.post('/admin/audit/route-not-found', { path })
    } catch {
      // Swallow - logging a 404 must never surface an error to the user.
    }
  }

  /**
   * Export the currently-filtered audit logs to Excel. Reuses the shared
   * downloadBlob helper (filename + timestamp handled there); the backend
   * reuses the platform-report XLSX exporter. Respects all current filters.
   */
  async exportLogsExcel(filters: AuditFilters = {}): Promise<void> {
    // Export spans the whole filtered set - pagination keys are meaningless to
    // the /export endpoint, so drop them rather than pass dead params.
    const qs = new URLSearchParams(
      Object.entries(filters)
        .filter(([k]) => k !== 'page' && k !== 'page_size')
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, String(v)])
    ).toString()
    await downloadBlob(`/admin/audit/export${qs ? `?${qs}` : ''}`, 'audit_logs')
  }
}

export const auditApi = new AuditApi()